import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Chip, RadioGroup, Radio } from '@nextui-org/react';
import { CtyButton } from '../button/CtyButton';
import { EMAIL_CONSTANTS } from '../utils/constants';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useShareConversationMailMutation } from '@circuitry-ai/doc-data';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import type { ShareConversationMailType } from '@circuitry-ai/doc-data';

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopyLink: () => void;
  isInviteLinkCopied: boolean;
  shareLink: string;
  senderId: string;
  advisorId: string;
}

function normalizeAndValidateEmail(raw: string): string | null {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed || !EMAIL_CONSTANTS.VALIDATION_REGEX.test(trimmed)) return null;
  return trimmed;
}

export function ShareModal({
  isOpen,
  onClose,
  onCopyLink,
  isInviteLinkCopied,
  shareLink,
  senderId,
  advisorId
}: Readonly<ShareModalProps>) {
  const { t } = useTranslation();
  const [emailChips, setEmailChips] = useState<string[]>([]);
  const [draftInput, setDraftInput] = useState('');
  const [mailFormat, setMailFormat] = useState<ShareConversationMailType>('text');
  const [shareConversationMail, { isLoading: isSendingMail }] = useShareConversationMailMutation();

  useEffect(() => {
    if (!isOpen) {
      setEmailChips([]);
      setDraftInput('');
      setMailFormat('text');
    }
  }, [isOpen]);

  const addEmail = useCallback((raw: string) => {
    const next = normalizeAndValidateEmail(raw);
    if (!next) return;
    setEmailChips((prev) => (prev.includes(next) ? prev : [...prev, next]));
    setDraftInput('');
  }, []);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const raw = (e.target as HTMLInputElement).value;
      if (e.key === 'Enter' || e.key === ',') {
        const toCommit = raw.trim();
        if (toCommit && EMAIL_CONSTANTS.VALIDATION_REGEX.test(toCommit)) {
          e.preventDefault();
          addEmail(toCommit);
        }
        return;
      }
      if (e.key === ' ') {
        const toCommit = raw.trim();
        if (toCommit && EMAIL_CONSTANTS.VALIDATION_REGEX.test(toCommit)) {
          e.preventDefault();
          addEmail(toCommit);
        }
      }
    },
    [addEmail]
  );

  const handleInputBlur = useCallback(() => {
    if (draftInput.trim()) {
      addEmail(draftInput);
    }
  }, [addEmail, draftInput]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text');
      if (!text.includes('@')) return;
      const parts = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      if (parts.length <= 1) return;
      const valid = parts.map((p) => normalizeAndValidateEmail(p)).filter((v): v is string => Boolean(v));
      if (valid.length === 0) return;
      e.preventDefault();
      setEmailChips((prev) => {
        const set = new Set(prev);
        valid.forEach((v) => set.add(v));
        return Array.from(set);
      });
      setDraftInput('');
    },
    []
  );

  const removeChip = useCallback((email: string) => {
    setEmailChips((prev) => prev.filter((x) => x !== email));
  }, []);

  const canSend = emailChips.length > 0 && Boolean(senderId && advisorId) && !isSendingMail;

  const handleSendMail = async () => {
    if (!canSend) return;
    try {
      const result = await shareConversationMail({
        type: mailFormat,
        emails: emailChips,
        sender_id: senderId,
        advisor_id: advisorId,
      }).unwrap();
      const msg = result?.message ?? t('Emails will be sent shortly');
      CtyShowToast(checkType.success, msg);
      setEmailChips([]);
      setDraftInput('');
    } catch (err: unknown) {
      const data = (err as { data?: { message?: string } })?.data;
      const message = data?.message ?? (err as Error)?.message ?? t('Failed to send email');
      CtyShowToast(checkType.error, message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      placement="center"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon icon="icon-park:share" width="1.2em" height="1.2em" className="dark:invert" />
            {t('Share Conversation')}
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <div className="text-sm font-medium">{t('Email')}<span className="text-default-500">*</span></div>
              <RadioGroup
                orientation="horizontal"
                size="sm"
                value={mailFormat}
                onValueChange={(v) => setMailFormat(v as ShareConversationMailType)}
                classNames={{ wrapper: 'gap-6' }}
              >
                <Radio value="text">{t('Text')}</Radio>
                <Radio value="pdf">{t('PDF')}</Radio>
              </RadioGroup>
              <div className="flex flex-col gap-2">
                {emailChips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {emailChips.map((email) => (
                      <Chip
                        key={email}
                        size="sm"
                        variant="flat"
                        color="primary"
                        onClose={() => removeChip(email)}
                      >
                        {email}
                      </Chip>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    aria-label={t('Recipient email addresses')}
                    type="text"
                    placeholder={EMAIL_CONSTANTS.PLACEHOLDER_EMAIL}
                    value={draftInput}
                    onValueChange={setDraftInput}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    onPaste={handlePaste}
                    className="flex-1"
                    classNames={{
                      input: 'text-sm',
                    }}
                  />
                  <CtyButton
                    color="primary"
                    isDisabled={!canSend}
                    isLoading={isSendingMail}
                    onPress={() => {
                      void handleSendMail();
                    }}
                    className="px-6"
                  >
                    {t('Send')}
                  </CtyButton>
                </div>
              </div>
            </div>

            <div className="text-center text-sm text-default-400">{t('or')}</div>

            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium">{t('Link to Share')}</div>
              <div className="flex gap-2">
                <Input
                  value={shareLink}
                  isReadOnly
                  className="flex-1"
                  classNames={{
                    input: "text-sm",
                  }}
                />
                <CtyButton
                  variant="bordered"
                  onPress={onCopyLink}
                  className="px-6"
                  color={isInviteLinkCopied ? "success" : "default"}
                >
                  {isInviteLinkCopied ? t('Copied') : t('Copy link')}
                </CtyButton>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <CtyButton variant="light" onPress={onClose} className="border-2 border-default-300">
            {t('Close')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
